import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  WxbAlert,
  WxbBadge,
  WxbButton,
  WxbCheckbox,
  WxbDataTable,
  WxbDatePicker,
  WxbInput,
  WxbInputNumber,
  WxbModal,
  WxbPageHeader,
  WxbPageSection,
  WxbPageShell,
  WxbPageToolbar,
  WxbSelect,
  WxbSpinner,
  WxbTableActionCell,
  WxbTag,
  WxbTextarea,
} from '../../components/wxb-ui';
import type { WxbTagColor } from '../../components/wxb-ui/Tag/Tag';
import { wxbToast } from '../../components/wxb-ui/Toast/Toast';
import { useAuth } from '../../contexts/AuthContext';
import {
  holidayCalendarApi,
  type HolidayCalendarEntry,
  type HolidayCalendarType,
  type UpdateHolidayCalendarPayload,
} from '../../services/holidayCalendarApi';
import {
  platformSiteApi,
  type PlatformSite,
} from '../../services/platformSiteApi';
import './HolidayCalendarManagementPage.css';

const HOLIDAY_TYPE_META: Record<HolidayCalendarType, { label: string; color: WxbTagColor }> = {
  LEGAL_HOLIDAY: { label: '法定节假日', color: 'red' },
  WEEKEND_ADJUSTMENT: { label: '调休休息日', color: 'blue' },
  MAKEUP_WORK: { label: '调休补班', color: 'amber' },
  WORKDAY: { label: '普通日期', color: 'neutral' },
};

const HOLIDAY_TYPE_OPTIONS = Object.entries(HOLIDAY_TYPE_META).map(([value, meta]) => ({
  value,
  label: meta.label,
}));

const SALARY_OPTIONS = [
  { value: 1, label: '1 倍（普通）' },
  { value: 2, label: '2 倍' },
  { value: 3, label: '3 倍' },
];

interface HolidayDraft extends UpdateHolidayCalendarPayload {
  calendarDate: string;
}

const createDraft = (year: number): HolidayDraft => ({
  calendarDate: `${year}-01-01`,
  isWorkday: false,
  holidayName: '',
  holidayType: 'LEGAL_HOLIDAY',
  notes: '',
  salaryOverride: false,
  salaryMultiplier: 3,
});

const entryToDraft = (entry: HolidayCalendarEntry): HolidayDraft => ({
  calendarDate: entry.calendarDate,
  isWorkday: entry.isWorkday,
  holidayName: entry.holidayName,
  holidayType: entry.holidayType,
  notes: entry.notes,
  salaryOverride: entry.salaryOverride,
  salaryMultiplier: entry.salaryMultiplier,
});

const weekdayLabel = (date: string): string => `周${'日一二三四五六'[dayjs(date).day()]}`;

const errorMessage = (error: unknown, fallback: string): string => {
  const response = (error as {
    response?: { data?: { error?: string } };
  })?.response?.data;
  return response?.error || fallback;
};

const HolidayCalendarManagementPage: React.FC = () => {
  const { currentSite } = useAuth();
  const [sites, setSites] = useState<PlatformSite[]>([]);
  const [sitePublicId, setSitePublicId] = useState('');
  const [year, setYear] = useState(dayjs().year());
  const [entries, setEntries] = useState<HolidayCalendarEntry[]>([]);
  const [loadingSites, setLoadingSites] = useState(true);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<HolidayDraft>(() => createDraft(dayjs().year()));
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadSites = async () => {
      setLoadingSites(true);
      try {
        const availableSites = (await platformSiteApi.listSites())
          .filter((site) => site.siteStatus !== 'RETIRED');
        if (cancelled) return;
        setSites(availableSites);
        const preferred = availableSites.find((site) => site.publicId === currentSite?.publicId)
          ?? availableSites[0];
        setSitePublicId(preferred?.publicId ?? '');
      } catch (error) {
        if (!cancelled) wxbToast.error(errorMessage(error, '加载 Site 列表失败'));
      } finally {
        if (!cancelled) setLoadingSites(false);
      }
    };
    void loadSites();
    return () => {
      cancelled = true;
    };
  }, [currentSite?.publicId]);

  const loadEntries = useCallback(async () => {
    if (!sitePublicId) {
      setEntries([]);
      return;
    }
    setLoadingEntries(true);
    setLoadError(false);
    try {
      const view = await holidayCalendarApi.listYear(sitePublicId, year);
      setEntries(view.entries);
    } catch (error) {
      setLoadError(true);
      wxbToast.error(errorMessage(error, '加载节假日日历失败'));
    } finally {
      setLoadingEntries(false);
    }
  }, [sitePublicId, year]);

  useEffect(() => {
    void loadEntries();
  }, [loadEntries]);

  const selectedSite = useMemo(
    () => sites.find((site) => site.publicId === sitePublicId),
    [sitePublicId, sites],
  );
  const tripleCount = entries.filter((entry) => entry.salaryMultiplier >= 3).length;
  const overrideCount = entries.filter((entry) => entry.salaryOverride).length;

  const openCreate = () => {
    setEditing(false);
    setDraft(createDraft(year));
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = useCallback((entry: HolidayCalendarEntry) => {
    setEditing(true);
    setDraft(entryToDraft(entry));
    setFormError(null);
    setModalOpen(true);
  }, []);

  const setHolidayType = (holidayType: HolidayCalendarType) => {
    setDraft((current) => ({
      ...current,
      holidayType,
      isWorkday: holidayType === 'MAKEUP_WORK'
        ? true
        : holidayType === 'LEGAL_HOLIDAY' || holidayType === 'WEEKEND_ADJUSTMENT'
          ? false
          : current.isWorkday,
      salaryMultiplier: holidayType === 'LEGAL_HOLIDAY' && !current.salaryOverride
        ? 3
        : current.salaryMultiplier,
    }));
  };

  const saveDraft = async () => {
    setFormError(null);
    if (!sitePublicId) {
      setFormError('请先选择 Site');
      return;
    }
    if (!draft.calendarDate || dayjs(draft.calendarDate).year() !== year) {
      setFormError(`日期必须属于 ${year} 年`);
      return;
    }
    if ((draft.holidayType !== 'WORKDAY' || draft.salaryOverride) && !draft.holidayName?.trim()) {
      setFormError('特殊日期或自定义工资倍数必须填写名称');
      return;
    }
    setSaving(true);
    try {
      await holidayCalendarApi.updateDate(sitePublicId, draft.calendarDate, {
        isWorkday: draft.isWorkday,
        holidayName: draft.holidayName?.trim() || null,
        holidayType: draft.holidayType,
        notes: draft.notes?.trim() || null,
        salaryOverride: draft.salaryOverride,
        salaryMultiplier: draft.salaryMultiplier,
      });
      wxbToast.success(editing ? '节假日配置已更新' : '特殊日期已新增');
      setModalOpen(false);
      await loadEntries();
    } catch (error) {
      setFormError(errorMessage(error, '保存节假日配置失败'));
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(() => [
    {
      title: '日期',
      key: 'date',
      render: (_: unknown, record: HolidayCalendarEntry) => (
        <div className="gv-holiday-date-cell">
          <span className="gv-holiday-date">{record.calendarDate}</span>
          <span className="gv-holiday-weekday">{weekdayLabel(record.calendarDate)}</span>
        </div>
      ),
    },
    {
      title: '节日',
      key: 'holiday',
      render: (_: unknown, record: HolidayCalendarEntry) => (
        <div className="gv-holiday-name-cell">
          <span>{record.holidayName || '未命名日期'}</span>
          <WxbTag color={HOLIDAY_TYPE_META[record.holidayType].color}>
            {HOLIDAY_TYPE_META[record.holidayType].label}
          </WxbTag>
        </div>
      ),
    },
    {
      title: '出勤属性',
      key: 'workday',
      render: (_: unknown, record: HolidayCalendarEntry) => (
        <WxbTag color={record.isWorkday ? 'green' : 'neutral'}>
          {record.isWorkday ? '工作日' : '休息日'}
        </WxbTag>
      ),
    },
    {
      title: '工资倍数',
      key: 'salary',
      render: (_: unknown, record: HolidayCalendarEntry) => (
        <div className="gv-holiday-salary-cell">
          <WxbTag color={record.salaryMultiplier >= 3 ? 'red' : record.salaryMultiplier >= 2 ? 'amber' : 'neutral'}>
            {record.salaryMultiplier} 倍
          </WxbTag>
          <span>{record.salaryOverride ? '管理员覆盖' : '日历默认'}</span>
        </div>
      ),
    },
    {
      title: '数据来源',
      dataIndex: 'calendarSource',
      key: 'calendarSource',
      render: (value: HolidayCalendarEntry['calendarSource']) => (
        <span className="gv-holiday-secondary">{value === 'MANUAL' ? '管理员维护' : value}</span>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 110,
      render: (_: unknown, record: HolidayCalendarEntry) => (
        <WxbTableActionCell
          actions={[{
            key: 'edit',
            label: '编辑',
            onClick: () => openEdit(record),
          }]}
        />
      ),
    },
  ], [openEdit]);

  return (
    <WxbPageShell size="wide">
      <WxbPageHeader
        eyebrow="平台控制面"
        title="节假日日历"
        description="维护各 Site 的法定节假日、调休补班与工资倍数。只有平台 Site 管理员可以访问和修改。"
        meta={!loadingEntries && !loadError ? (
          <WxbBadge variant="bar" status="info" label={`${year} 年 · ${entries.length} 个特殊日期`} />
        ) : undefined}
        actions={<WxbButton variant="primary" onClick={openCreate} disabled={!sitePublicId}>新增特殊日期</WxbButton>}
      />

      <WxbAlert title="法定节假日默认按三薪处理">
        系统直接以工作日历中的“法定节假日”作为三薪日，不再使用固定日期或春节偏移推算。只有确有例外时才开启工资倍数覆盖。
      </WxbAlert>

      <WxbPageSection
        title="年度日历"
        description={selectedSite ? `${selectedSite.siteName}（${selectedSite.siteCode}）` : '请选择 Site'}
        variant="framed"
      >
        <WxbPageToolbar
          filters={(
            <div className="gv-holiday-filters">
              <WxbSelect
                label="Site"
                aria-label="选择 Site"
                value={sitePublicId || undefined}
                loading={loadingSites}
                options={sites.map((site) => ({
                  value: site.publicId,
                  label: `${site.siteName}（${site.siteCode}）`,
                }))}
                onChange={(value) => setSitePublicId(String(value))}
              />
              <WxbInputNumber
                label="年份"
                aria-label="年份"
                min={2000}
                max={2100}
                precision={0}
                value={year}
                onChange={(value) => {
                  if (typeof value === 'number') setYear(value);
                }}
              />
            </div>
          )}
          summary={`三薪日 ${tripleCount} 天 · 人工工资覆盖 ${overrideCount} 天`}
          actions={<WxbButton variant="secondary" onClick={() => void loadEntries()} disabled={!sitePublicId || loadingEntries}>刷新</WxbButton>}
        />

        {loadingEntries ? (
          <div className="gv-holiday-loading">
            <WxbSpinner tip="加载节假日日历中…" />
          </div>
        ) : (
          <WxbDataTable<HolidayCalendarEntry>
            rowKey="calendarDate"
            columns={columns}
            dataSource={entries}
            pagination={false}
            errorState={loadError ? {
              title: '加载失败',
              description: '无法读取所选 Site 的节假日日历',
              action: <WxbButton variant="secondary" onClick={() => void loadEntries()}>重试</WxbButton>,
            } : undefined}
            emptyState={{
              description: sitePublicId
                ? `${year} 年尚无特殊日期；可以新增日期或等待年度日历导入`
                : '暂无可维护的 Site',
            }}
          />
        )}
      </WxbPageSection>

      <WxbModal
        open={modalOpen}
        title={editing ? '编辑节假日' : '新增特殊日期'}
        width={640}
        okText="保存"
        confirmLoading={saving}
        destroyOnClose
        onCancel={() => setModalOpen(false)}
        onOk={saveDraft}
      >
        <div className="gv-holiday-form">
          <div className="gv-holiday-form-row">
            <WxbDatePicker
              label="日期"
              aria-label="日期"
              value={draft.calendarDate ? dayjs(draft.calendarDate) : null}
              disabled={editing}
              onChange={(value) => setDraft((current) => ({
                ...current,
                calendarDate: value ? value.format('YYYY-MM-DD') : '',
              }))}
            />
            <WxbSelect
              label="日期类型"
              aria-label="日期类型"
              value={draft.holidayType}
              options={HOLIDAY_TYPE_OPTIONS}
              onChange={(value) => setHolidayType(value as HolidayCalendarType)}
            />
          </div>
          <WxbInput
            label="节日或调休名称"
            aria-label="节日或调休名称"
            value={draft.holidayName ?? ''}
            maxLength={100}
            placeholder="例如：春节、国庆节、春节调休"
            onChange={(event) => setDraft((current) => ({
              ...current,
              holidayName: event.target.value,
            }))}
          />
          <WxbCheckbox
            checked={draft.isWorkday}
            disabled={draft.holidayType === 'LEGAL_HOLIDAY'
              || draft.holidayType === 'WEEKEND_ADJUSTMENT'
              || draft.holidayType === 'MAKEUP_WORK'}
            onChange={(checked) => setDraft((current) => ({ ...current, isWorkday: checked }))}
          >
            当天需要上班
          </WxbCheckbox>
          <div className="gv-holiday-salary-editor">
            <WxbCheckbox
              checked={draft.salaryOverride}
              onChange={(checked) => setDraft((current) => ({
                ...current,
                salaryOverride: checked,
                salaryMultiplier: checked
                  ? current.salaryMultiplier
                  : current.holidayType === 'LEGAL_HOLIDAY' ? 3 : 1,
              }))}
            >
              自定义工资倍数
            </WxbCheckbox>
            <WxbSelect
              label="工资倍数"
              aria-label="工资倍数"
              value={draft.salaryMultiplier}
              disabled={!draft.salaryOverride}
              options={SALARY_OPTIONS}
              onChange={(value) => setDraft((current) => ({
                ...current,
                salaryMultiplier: Number(value),
              }))}
            />
          </div>
          <WxbTextarea
            label="备注"
            aria-label="备注"
            value={draft.notes ?? ''}
            maxLength={255}
            placeholder="记录政策来源或本次调整原因"
            onChange={(event) => setDraft((current) => ({
              ...current,
              notes: event.target.value,
            }))}
          />
          {formError ? <div className="gv-holiday-form-error" role="alert">{formError}</div> : null}
        </div>
      </WxbModal>
    </WxbPageShell>
  );
};

export default HolidayCalendarManagementPage;
